import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import PlaylistIcon from '@atlaskit/icon-lab/core/playlist';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	playlistHeader: {
		id: 'ai-audio.playlist.header',
		defaultMessage: 'Playlist',
		description: 'Header for the playlist',
	},
	collapsePlaylistLabel: {
		id: 'ai-audio.playlist.header.collapse-label',
		defaultMessage: 'Collapse playlist',
		description: 'Accessible label for collapsing the playlist',
	},
	expandPlaylistLabel: {
		id: 'ai-audio.playlist.header.expand-label',
		defaultMessage: 'Expand playlist',
		description: 'Accessible label for expanding the playlist',
	},
} as const);

interface PlaylistHeaderProps {
	isExpanded: boolean;
	onToggle: () => void;
}

const styles = cssMap({
	container: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		position: 'sticky',
		top: 0,
		zIndex: 1,
		height: `40px`, //safari sticky elements need a fixed height in order not to flicker
		backgroundColor: token('elevation.surface'),
		transform: 'translate3d(0, 0, 0)', //safari Flicker fix hack
		// @ts-ignore
		'-webkit-backface-visibility': 'hidden', //safari Flicker fix hack
	},
	containerExpanded: {
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
		paddingTop: token('space.075'),
	},
	headerButton: {
		height: '32px',
		cursor: 'pointer',
		width: '100%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		color: token('color.text.subtle'),
		font: token('font.heading.xsmall'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-visible': {
			outlineOffset: token('space.0'),
		},
	},
	cardWrapper: {
		height: '100%',
	},
});

export const PlaylistHeader: React.FC<PlaylistHeaderProps> = ({ isExpanded, onToggle }) => {
	const { formatMessage } = useIntl();

	return (
		<Box id="audio-player-changeboarding-step-2" xcss={styles.cardWrapper}>
			<Box xcss={cx(styles.container, isExpanded && styles.containerExpanded)}>
			<Heading as="h3" size="xsmall">
				<Pressable
					onClick={onToggle}
					xcss={styles.headerButton}
					aria-label={formatMessage(
						isExpanded ? messages.collapsePlaylistLabel : messages.expandPlaylistLabel,
					)}
				>
					<Flex justifyContent="space-between" alignItems="center">
						<Flex gap="space.100" alignItems="center">
							<PlaylistIcon label="" />

								{formatMessage(messages.playlistHeader)}

						</Flex>
						{isExpanded ? (
							<ChevronDownIcon label="" size="medium" />
						) : (
							<ChevronUpIcon label="" size="medium" />
						)}
					</Flex>
				</Pressable>
				</Heading>
			</Box>
		</Box>
	);
};
